import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
    Bars3Icon,
    XMarkIcon,
    EyeIcon,
    EyeSlashIcon,
    ChevronDownIcon,
    BoltIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useLayout } from '@/contexts/LayoutContext';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { useGoApi } from '@/contexts/GoApiContext';
import { useInstallPrompt } from '@/contexts/InstallPromptContext';
import { useUpdateUserSettings } from '@/api/hooks/useUserSettings';
import { toast } from 'sonner';

interface HeaderProps {
    isMobileMenuOpen: boolean;
    onToggleMobileMenu: () => void;
}

export function Header({
    isMobileMenuOpen,
    onToggleMobileMenu,
}: HeaderProps) {
    const { settings, isAdmin } = useAuth();
    const { isFullWidth } = useLayout();
    const { isPrivacyModeEnabled, togglePrivacyMode } = usePrivacy();
    const { goApiEnabled, toggleGoApi } = useGoApi();
    const { isStandalone } = useInstallPrompt();
    const updateSettings = useUpdateUserSettings();

    const currencies = settings?.currencies || [];
    const displayCurrency = settings?.displayCurrency || 'USD';

    // Add SDR to currencies if enabled
    const availableCurrencies = settings?.showNetWorthInSdr
        ? [...currencies, 'SDR']
        : currencies;

    const handleCurrencyChange = async (currency: string) => {
        try {
            await updateSettings.mutateAsync({ displayCurrency: currency });
        } catch (error) {
            toast.error('Failed to update display currency');
        }
    };

    // Currency dropdown component (reused for mobile and desktop)
    const CurrencyDropdown = ({ buttonClassName }: { buttonClassName?: string }) => (
        <Menu as="div" className="relative">
            <Menu.Button className={buttonClassName || "flex items-center gap-1 cursor-pointer rounded-md border border-gray-200 bg-white px-3 py-2 shadow-xs transition-colors hover:border-accent focus:border-accent focus:outline-hidden focus:ring-3 focus:ring-accent/50 dark:border-gray-700 dark:bg-secondary dark:text-white dark:focus:ring-accent/50"}>
                {displayCurrency}
                <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute right-0 mt-2 min-w-[80px] origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-hidden dark:border-gray-600 dark:bg-dark max-h-60 overflow-y-auto z-50">
                    {availableCurrencies.length > 0 ? (
                        availableCurrencies.map((currency, index) => {
                            const isSelected = currency === displayCurrency;
                            return (
                                <Menu.Item key={currency}>
                                    {({ active }) => (
                                        <button
                                            onClick={() => handleCurrencyChange(currency)}
                                            className={`${
                                                active
                                                    ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                                                    : isSelected
                                                        ? 'bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200'
                                                        : 'text-gray-900 dark:text-gray-100'
                                            } ${index === 0 ? 'rounded-t-lg' : ''} ${index === availableCurrencies.length - 1 ? 'rounded-b-lg' : ''} w-full py-2 px-4 text-left text-sm cursor-pointer select-none`}
                                        >
                                            <span className={isSelected ? 'font-medium' : 'font-normal'}>
                                                {currency}
                                            </span>
                                        </button>
                                    )}
                                </Menu.Item>
                            );
                        })
                    ) : (
                        <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 rounded-lg">
                            No currencies configured
                        </div>
                    )}
                </Menu.Items>
            </Transition>
        </Menu>
    );

    // Privacy toggle button component (reused for mobile and desktop)
    const PrivacyToggle = () => (
        <button
            onClick={togglePrivacyMode}
            type="button"
            className="inline-flex items-center justify-center rounded-md bg-gray-50 p-2 text-gray-700 transition-colors hover:bg-gray-100 focus:outline-hidden focus:ring-3 focus:ring-offset-2 dark:bg-dark dark:text-primary-light dark:hover:bg-primary"
            title={isPrivacyModeEnabled ? 'Show values' : 'Hide values'}
        >
            <span className="sr-only">Toggle privacy mode</span>
            {isPrivacyModeEnabled ? (
                <EyeSlashIcon className="h-7 w-7" />
            ) : (
                <EyeIcon className="h-7 w-7" />
            )}
        </button>
    );

    return (
        <div className="sticky top-0 z-40 w-full shrink-0 bg-white dark:bg-darker">
            <div className="flex h-[57px] w-full items-center border-b border-gray-300 dark:border-gray-700">
                <div className="w-full px-4 md:px-6 lg:px-8">
                    <div className={`flex w-full items-center justify-between ${isFullWidth ? '' : 'max-w-(--breakpoint-2xl)'}`}>
                        {/* Left side - Mobile menu button (mobile only) */}
                        <div className="flex items-center lg:hidden">
                            <button
                                onClick={onToggleMobileMenu}
                                type="button"
                                data-mobile-menu-toggle
                                className={`inline-flex items-center justify-center rounded-md p-1.5 transition-colors focus:outline-hidden focus:ring-3 focus:ring-offset-2 ${
                                    isMobileMenuOpen
                                        ? 'bg-gray-300 text-gray-900 hover:bg-gray-400 dark:bg-secondary dark:text-white dark:hover:bg-accent'
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-dark dark:text-primary-light dark:hover:bg-primary'
                                }`}
                            >
                                <span className="sr-only">Toggle main menu</span>
                                {isMobileMenuOpen ? (
                                    <XMarkIcon className="h-8 w-8" />
                                ) : (
                                    <Bars3Icon className="h-8 w-8" />
                                )}
                            </button>
                        </div>

                        {/* Empty div for desktop left side (keeps justify-between working) */}
                        <div className="hidden lg:block" />

                        {/* Right side - Currency and Privacy controls */}
                        <nav
                            aria-label="Secondary"
                            className="flex items-center space-x-2"
                        >
                            <CurrencyDropdown />
                            {isAdmin && (
                                <button
                                    onClick={toggleGoApi}
                                    type="button"
                                    className={`inline-flex items-center justify-center rounded-md p-2 transition-colors focus:outline-hidden focus:ring-3 focus:ring-offset-2 ${
                                        goApiEnabled
                                            ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800'
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-dark dark:text-primary-light dark:hover:bg-primary'
                                    }`}
                                    title={goApiEnabled ? 'Go API enabled' : 'Go API disabled'}
                                >
                                    <span className="sr-only">Toggle Go API</span>
                                    <BoltIcon className="h-7 w-7" />
                                </button>
                            )}
                            {/* Home-screen app has no browser reload control; full reload also picks up a new deploy */}
                            <button
                                onClick={() => window.location.reload()}
                                type="button"
                                className={`inline-flex items-center justify-center rounded-md bg-gray-50 p-2 text-gray-700 transition-colors hover:bg-gray-100 focus:outline-hidden focus:ring-3 focus:ring-offset-2 dark:bg-dark dark:text-primary-light dark:hover:bg-primary ${
                                    isStandalone ? '' : 'lg:hidden'
                                }`}
                                title="Refresh"
                            >
                                <span className="sr-only">Refresh page</span>
                                <ArrowPathIcon className="h-7 w-7" />
                            </button>
                            <PrivacyToggle />
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    );
}
